import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  updateDoc, 
  query, 
  where,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import {
  getOrderedEliminationRows,
  getResultRowContestantId,
  getVotedOutContestants as getVotedOutListFromResults,
  getVotedOutNames,
  isOrderedEliminationResults,
  normalizeContestantToName as normIdOrNameToDisplayName,
  wasContestantEliminatedPreviously,
} from '../utils/scoringHelpers';
import { computeWeekScore, toPersistedWeekBreakdown } from '../utils/weekScoreEngine';

export class ScoreService {
  /**
   * Get contestants map (name to ID mapping)
   * @returns {Object} Map of contestant names to IDs
   */
  static async getContestantsMap() {
    try {
      const contestantsSnapshot = await getDocs(collection(db, 'contestants'));
      const contestantsMap = {};
      
      contestantsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.name) {
          contestantsMap[data.name] = doc.id;
        }
      });
      
      return contestantsMap;
    } catch (error) {
      console.error('Error getting contestants map:', error);
      return {};
    }
  }

  /**
   * Get season-scoped contestant ID -> name map
   * @param {string} seasonId
   * @returns {Object} Map of contestantId to name
   */
  static async getSeasonContestantIdToNameMap(seasonId) {
    try {
      // Get contestants from the season document itself
      const seasonDoc = await getDoc(doc(db, 'seasons', seasonId));
      if (!seasonDoc.exists()) {
        console.error('Season document not found:', seasonId);
        return {};
      }
      
      const seasonData = seasonDoc.data();
      const contestants = seasonData.contestants || [];
      
      // Get global contestants to map IDs to names
      const globalContestantsSnapshot = await getDocs(collection(db, 'contestants'));
      const globalContestantsMap = {};
      globalContestantsSnapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data && data.name) {
          globalContestantsMap[docSnap.id] = data.name;
        }
      });
      
      const idToName = {};
      contestants.forEach(contestant => {
        // Handle both string IDs and object contestants
        if (typeof contestant === 'string') {
          // Map season ID to global contestant name
          const globalName = globalContestantsMap[contestant];
          if (globalName) {
            idToName[contestant] = globalName;
          } else {
            // Fallback to ID if no global name found
            idToName[contestant] = contestant;
          }
        } else if (contestant.id && contestant.name) {
          idToName[contestant.id] = contestant.name;
        }
      });
      
      console.log('Found contestants in season:', contestants.length);
      console.log('Sample contestants:', contestants.slice(0, 3));
      console.log('Sample idToName mapping:', Object.entries(idToName).slice(0, 3));
      
      return idToName;
    } catch (error) {
      console.error('Error getting season contestant map:', error);
      return {};
    }
  }

  static getVotedOutContestants(results) {
    return getVotedOutListFromResults(results);
  }

  static normalizeContestantToName(contestant, contestantsMap = null) {
    return normIdOrNameToDisplayName(contestant, contestantsMap);
  }

  static getVotedOutNames(results, contestantsMap = null) {
    return getVotedOutNames(results, contestantsMap);
  }

  static isOrderedEliminationResults(results) {
    return isOrderedEliminationResults(results);
  }

  static getOrderedEliminationRows(results) {
    return getOrderedEliminationRows(results);
  }

  static getResultRowContestantId(row, seasonContestantsMap) {
    return getResultRowContestantId(row, seasonContestantsMap);
  }

  static wasContestantEliminatedPreviously(
    contestantName,
    seasonData,
    weekNumber,
    contestantsMap = null,
  ) {
    return wasContestantEliminatedPreviously(
      contestantName,
      seasonData,
      weekNumber,
      contestantsMap,
    );
  }

  /**
   * Calculate scores for all teams in a league for a specific week
   * @param {string} leagueId - League ID
   * @param {string} seasonId - Season ID
   * @param {number} weekNumber - Week number
   * @returns {Promise<Object>} Results of score calculation
   */
  static async calculateScoresForWeek(leagueId, seasonId, weekNumber) {
    try {
      console.log(`Starting score calculation for League ${leagueId}, Season ${seasonId}, Week ${weekNumber}`);
      
      // Get all teams in the league-season
      console.log('Fetching teams...');
      const teamsSnapshot = await getDocs(
        collection(db, 'leagues', leagueId, 'seasons', seasonId, 'teams')
      );
      console.log(`Found ${teamsSnapshot.docs.length} teams`);
      
      if (teamsSnapshot.empty) {
        console.log('No teams found for this league');
        return { success: false, message: 'No teams found for this league' };
      }

      // Get season data
      console.log('Fetching season data...');
      const seasonDoc = await getDoc(doc(db, 'seasons', seasonId));
      if (!seasonDoc.exists()) {
        console.log('Season not found');
        return { success: false, message: 'Season not found' };
      }

      const seasonData = seasonDoc.data();
      console.log('Season data loaded');
      const week = seasonData.weeks?.find(w => w.weekNumber === weekNumber);
      
      if (!week) {
        console.log('Week not found');
        return { success: false, message: 'Week not found' };
      }

      if (!week.results) {
        console.log('Week has no results');
        return { success: false, message: 'Week has no results' };
      }
      console.log('Week results found:', week.results);

      // Get league-season data for scoring rules
      console.log('Fetching league-season data...');
      const leagueSeasonDoc = await getDoc(doc(db, 'leagues', leagueId, 'seasons', seasonId));
      if (!leagueSeasonDoc.exists()) {
        console.log('League-season not found');
        return { success: false, message: 'League-season not found' };
      }

      const leagueSeasonData = leagueSeasonDoc.data();
      const scoringRules = leagueSeasonData.scoringRules || {};
      console.log('League-season data loaded, scoring rules:', scoringRules);
      console.log('Full league-season data keys:', Object.keys(leagueSeasonData));
      console.log('League-season data sample:', JSON.stringify(leagueSeasonData, null, 2));

      // Get contestants map for normalization - use season-specific contestants
      console.log('Fetching season contestants map...');
      const seasonContestantsMap = await this.getSeasonContestantIdToNameMap(seasonId);
      console.log('Season contestants map loaded:', Object.keys(seasonContestantsMap).length, 'contestants');
      console.log('Sample season contestants map:', Object.entries(seasonContestantsMap).slice(0, 5));
      console.log('Looking for contestant-49-2 in map:', seasonContestantsMap['contestant-49-2']);
      console.log('All keys containing "contestant-49-2":', Object.keys(seasonContestantsMap).filter(key => key.includes('contestant-49-2')));

      // Get all predictions for this week
      console.log('Fetching predictions...');
      const predictionsSnapshot = await getDocs(
        query(
          collection(db, 'predictions'),
          where('leagueId', '==', leagueId),
          where('seasonId', '==', seasonId),
          where('weekNumber', '==', weekNumber)
        )
      );
      console.log(`Found ${predictionsSnapshot.docs.length} predictions`);

      const predictionsByTeam = {};
      predictionsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const teamId = data.teamId;
        if (!predictionsByTeam[teamId]) {
          predictionsByTeam[teamId] = [];
        }
        predictionsByTeam[teamId].push(data);
      });
      console.log('Predictions organized by team');

      // Calculate scores for each team
      console.log('Starting score calculations...');
      const batch = writeBatch(db);
      let updatedTeams = 0;

      for (const teamDoc of teamsSnapshot.docs) {
        console.log(`Processing team ${teamDoc.id}...`);
        const teamData = teamDoc.data();
        const teamId = teamDoc.id;
        
        // Get predictions for this team
        const teamPredictions = predictionsByTeam[teamId] || [];

        const weekBreakdown = computeWeekScore(
          teamData,
          teamPredictions,
          week.results,
          weekNumber,
          scoringRules,
          seasonData,
          seasonContestantsMap,
          seasonData?.mergeWeek ?? null,
        );
        const weeklyScore = weekBreakdown.total;

        // Update team's weekly scores
        const currentWeeklyScores = teamData.weeklyScores || {};
        currentWeeklyScores[weekNumber.toString()] = weeklyScore;

        const currentBreakdowns = teamData.weeklyScoreBreakdowns || {};
        currentBreakdowns[weekNumber.toString()] = toPersistedWeekBreakdown(
          weekBreakdown,
          new Date().toISOString(),
        );

        // Calculate new total score
        const totalScore = Object.values(currentWeeklyScores).reduce(
          (sum, score) => sum + score,
          0,
        );

        // Update team document
        const teamRef = doc(db, 'leagues', leagueId, 'seasons', seasonId, 'teams', teamId);
        batch.update(teamRef, {
          weeklyScores: currentWeeklyScores,
          weeklyScoreBreakdowns: currentBreakdowns,
          currentScore: totalScore,
          updatedAt: new Date(),
        });

        console.log(`Updated team ${teamId} in league ${leagueId}: weeklyScores=${JSON.stringify(currentWeeklyScores)}, currentScore=${totalScore}`);
        updatedTeams++;
      }

      // Commit all updates
      await batch.commit();

      console.log(`Score calculation complete. Updated ${updatedTeams} teams.`);
      return { 
        success: true, 
        message: `Successfully calculated scores for ${updatedTeams} teams`,
        updatedTeams 
      };

    } catch (error) {
      console.error('Error calculating scores:', error);
      return { 
        success: false, 
        message: `Error calculating scores: ${error.message}` 
      };
    }
  }

  /**
   * Recalculate scores for all teams in a league for a specific week
   * @param {string} seasonId - Season ID
   * @param {number} weekNumber - Week number
   * @param {Object} results - Week results
   * @param {boolean} isFinale - Whether this is a finale week
   * @returns {Promise<Object>} Results of score recalculation
   */
  static async recalculateScoresForSpecificWeek(seasonId, weekNumber, results, isFinale) {
    console.log(`Recalculating scores for Season ${seasonId}, Week ${weekNumber}`);
    
    try {
      // Get season data to find the league
      console.log('Fetching season document...');
      const seasonDoc = await getDoc(doc(db, 'seasons', seasonId));
      console.log('Season document fetched');
      
      if (!seasonDoc.exists()) {
        console.log('Season not found');
        return { success: false, message: 'Season not found' };
      }

      const seasonData = seasonDoc.data();
      console.log('Season data:', seasonData);
      
      // For admin seasons, we need to find all leagues that use this season
      console.log('Finding leagues that use this season...');
      const leaguesSnapshot = await getDocs(collection(db, 'leagues'));
      const leaguesUsingSeason = [];
      
      for (const leagueDoc of leaguesSnapshot.docs) {
        const leagueData = leagueDoc.data();
        // Check if this league has a season with the same seasonId
        const leagueSeasonsSnapshot = await getDocs(
          collection(db, 'leagues', leagueDoc.id, 'seasons')
        );
        
        for (const seasonDoc of leagueSeasonsSnapshot.docs) {
          if (seasonDoc.id === seasonId) {
            leaguesUsingSeason.push(leagueDoc.id);
            break;
          }
        }
      }
      
      console.log(`Found ${leaguesUsingSeason.length} leagues using this season:`, leaguesUsingSeason);
      
      if (leaguesUsingSeason.length === 0) {
        return { success: false, message: 'No leagues found using this season' };
      }
      
      // Calculate scores for each league
      const calculationResults = [];
      let teamsUpdated = 0;
      let leaguesProcessed = 0;
      
      for (const leagueId of leaguesUsingSeason) {
        console.log(`Calculating scores for league ${leagueId}...`);
        const result = await this.calculateScoresForWeek(leagueId, seasonId, weekNumber);
        calculationResults.push({ leagueId, result });
        if (result.success) {
          teamsUpdated += result.updatedTeams || 0;
          leaguesProcessed++;
        }
      }
      
      // Record the calculation timestamp in the season's week data
      // Note: Can't use serverTimestamp() in arrays, so we use Timestamp.now() instead
      const updatedWeeks = [...(seasonData.weeks || [])];
      const weekIndex = updatedWeeks.findIndex(w => w.weekNumber === weekNumber);
      if (weekIndex !== -1) {
        const week = updatedWeeks[weekIndex];
        if (!week.scoreCalculationHistory) {
          week.scoreCalculationHistory = [];
        }
        week.scoreCalculationHistory.push({
          calculatedAt: Timestamp.now(),
          scope: 'week'
        });
        
        // Update the season document
        await updateDoc(doc(db, 'seasons', seasonId), {
          weeks: updatedWeeks
        });
      }
      
      return { 
        success: true, 
        message: `Successfully calculated scores for ${leaguesProcessed} leagues`,
        teamsUpdated,
        leaguesProcessed,
        results: calculationResults
      };
    } catch (error) {
      console.error('Error in recalculateScoresForSpecificWeek:', error);
      return { 
        success: false, 
        message: `Error recalculating scores: ${error.message}` 
      };
    }
  }

  /**
   * Recalculate scores for all weeks in a season
   * @param {string} seasonId - Season ID
   * @returns {Promise<Object>} Results of score recalculation
   */
  static async recalculateScoresForSeason(seasonId) {
    console.log(`Recalculating scores for entire Season ${seasonId}`);
    
    try {
      // Get season data
      const seasonDoc = await getDoc(doc(db, 'seasons', seasonId));
      if (!seasonDoc.exists()) {
        return { success: false, message: 'Season not found' };
      }

      const seasonData = seasonDoc.data();
      const weeks = seasonData.weeks || [];
      
      // Find all leagues using this season
      const leaguesSnapshot = await getDocs(collection(db, 'leagues'));
      const leaguesUsingSeason = [];
      
      for (const leagueDoc of leaguesSnapshot.docs) {
        const leagueSeasonsSnapshot = await getDocs(
          collection(db, 'leagues', leagueDoc.id, 'seasons')
        );
        
        for (const seasonDoc of leagueSeasonsSnapshot.docs) {
          if (seasonDoc.id === seasonId) {
            leaguesUsingSeason.push(leagueDoc.id);
            break;
          }
        }
      }
      
      if (leaguesUsingSeason.length === 0) {
        return { success: false, message: 'No leagues found using this season' };
      }
      
      let totalTeamsUpdated = 0;
      let totalLeaguesProcessed = 0;
      const updatedWeeks = [...weeks];
      
      // Calculate scores for each week that has results
      for (let weekIndex = 0; weekIndex < updatedWeeks.length; weekIndex++) {
        const week = updatedWeeks[weekIndex];
        
        // Only calculate scores for weeks that have results
        if (week.results && Object.keys(week.results).length > 0) {
          console.log(`Calculating scores for Week ${week.weekNumber}...`);
          
          // Calculate scores for each league
          for (const leagueId of leaguesUsingSeason) {
            const result = await this.calculateScoresForWeek(leagueId, seasonId, week.weekNumber);
            if (result.success) {
              totalTeamsUpdated += result.updatedTeams || 0;
            }
          }
          
          totalLeaguesProcessed += leaguesUsingSeason.length;
          
          // Record the calculation timestamp for this week
          // Note: Use Timestamp.now() for each week individually so each week gets its own timestamp
          // Can't use serverTimestamp() in arrays, so we use Timestamp.now() instead
          if (!week.scoreCalculationHistory) {
            week.scoreCalculationHistory = [];
          }
          week.scoreCalculationHistory.push({
            calculatedAt: Timestamp.now(),
            scope: 'season'
          });
        }
      }
      
      // Update the season document with all week updates
      await updateDoc(doc(db, 'seasons', seasonId), {
        weeks: updatedWeeks
      });
      
      return { 
        success: true, 
        message: `Successfully calculated scores for ${totalLeaguesProcessed} league-week combinations`,
        teamsUpdated: totalTeamsUpdated,
        leaguesProcessed: leaguesUsingSeason.length,
        weeksProcessed: updatedWeeks.filter(w => w.results && Object.keys(w.results).length > 0).length
      };
    } catch (error) {
      console.error('Error in recalculateScoresForSeason:', error);
      return { 
        success: false, 
        message: `Error recalculating scores: ${error.message}` 
      };
    }
  }
}